/**
 * Inline message formatting.
 *
 * Chat messages are stored as plain text. The composer's text-style toolbar
 * wraps the selection in the markers below (the same markers the Gripp OS
 * desktop composer inserts), and this parser turns them back into styled
 * segments at render time so the markers never reach the reader.
 *
 * Deliberately NOT a general markdown implementation: only the marks the
 * toolbar can produce are recognised, and each rule is written to be
 * conservative so ordinary prose that happens to contain a `*`, `_` or `~`
 * keeps rendering literally. Widening this set means widening what an already
 * sent message can suddenly turn into, so add rules only alongside a toolbar
 * button that produces them.
 */

/** Marker pairs the composer writes and this module reads back. */
export const messageFormatMarkers = {
  bold: { open: '**', close: '**' },
  italic: { open: '_', close: '_' },
  underline: { open: '<u>', close: '</u>' },
  strikethrough: { open: '~', close: '~' },
} as const;

export type MessageFormatMark = keyof typeof messageFormatMarkers;

/** A run of text sharing one set of marks. `url` marks it as a link label. */
export type FormattedTextSegment = {
  text: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strikethrough?: boolean;
  url?: string;
};

type ActiveMarks = Omit<FormattedTextSegment, 'text' | 'url'>;

/**
 * `[label](url)` — label may not span lines, url may not contain whitespace and
 * must be http(s). Message text is cross-user content, so other schemes
 * (`javascript:`, `tel:`, custom app schemes) must not become tappable under a
 * label that hides them; they render literally instead.
 */
const linkPattern = /^\[([^\]\n]+)\]\((https?:\/\/[^)\s]+)\)/i;

/**
 * `_` and `~` only open/close a mark at a word boundary, so `snake_case_name`
 * survives untouched and `5~10~15` stays a numeric range rather than losing
 * its middle to a strikethrough.
 */
const isWordCharacter = (character: string | undefined): boolean =>
  character !== undefined && /[A-Za-z0-9_]/.test(character);

const rules: {
  mark: MessageFormatMark;
  open: string;
  close: string;
  /** Require non-word characters on both outer edges (single-char markers). */
  wordBoundary?: boolean;
}[] = [
  { mark: 'bold', ...messageFormatMarkers.bold },
  { mark: 'underline', ...messageFormatMarkers.underline },
  {
    mark: 'strikethrough',
    ...messageFormatMarkers.strikethrough,
    wordBoundary: true,
  },
  { mark: 'italic', ...messageFormatMarkers.italic, wordBoundary: true },
];

/**
 * Content between markers must be non-empty, must not start or end with a
 * space (so "2 * 3 * 4" and "a ~ b ~ c" stay arithmetic/prose), and must not
 * cross a blank line (an unclosed marker then swallows whole paragraphs).
 */
const isValidContent = (content: string): boolean =>
  content.length > 0 &&
  !content.startsWith(' ') &&
  !content.endsWith(' ') &&
  !content.includes('\n\n');

/**
 * Splits `text` into styled segments. Marks nest, so `**_both_**` yields one
 * segment that is bold and italic.
 */
export const parseFormattedText = (
  text: string,
  marks: ActiveMarks = {}
): FormattedTextSegment[] => {
  const segments: FormattedTextSegment[] = [];
  let plainStart = 0;
  let index = 0;

  const flushPlain = (until: number) => {
    if (until > plainStart) {
      segments.push({ ...marks, text: text.slice(plainStart, until) });
    }
  };

  while (index < text.length) {
    if (text[index] === '[') {
      const link = linkPattern.exec(text.slice(index));
      if (link) {
        flushPlain(index);
        segments.push({ ...marks, text: link[1], url: link[2] });
        index += link[0].length;
        plainStart = index;
        continue;
      }
    }

    const rule = rules.find((candidate) => {
      if (!text.startsWith(candidate.open, index)) return false;
      if (marks[candidate.mark]) return false; // already inside this mark
      if (candidate.wordBoundary && isWordCharacter(text[index - 1])) {
        return false;
      }

      const contentStart = index + candidate.open.length;
      const closeIndex = text.indexOf(candidate.close, contentStart);
      if (closeIndex === -1) return false;
      if (!isValidContent(text.slice(contentStart, closeIndex))) return false;

      return !(
        candidate.wordBoundary &&
        isWordCharacter(text[closeIndex + candidate.close.length])
      );
    });

    if (rule) {
      const contentStart = index + rule.open.length;
      const closeIndex = text.indexOf(rule.close, contentStart);
      flushPlain(index);
      segments.push(
        ...parseFormattedText(text.slice(contentStart, closeIndex), {
          ...marks,
          [rule.mark]: true,
        })
      );
      index = closeIndex + rule.close.length;
      plainStart = index;
      continue;
    }

    index += 1;
  }

  flushPlain(text.length);
  return segments;
};

/**
 * Turns the `- ` line prefixes the bullet-list button writes into real bullet
 * glyphs. Ordered lists (`1. `) already read as intended and are left alone.
 */
/**
 * The message as a reader sees it, with the formatting markers removed.
 *
 * For the places that show message text as PLAIN text and cannot render
 * segments — a derived Issue title, a one-line activity preview — where the
 * raw string would show `**bold**` literally. Runs through the same parser the
 * renderer uses, so "what counts as a marker" stays defined in one place.
 */
export const stripFormattingMarkers = (text: string): string =>
  parseFormattedText(text)
    .map((segment) => segment.text)
    .join('');

export const renderListMarkers = (text: string): string =>
  text.replace(/^([ \t]*)- /gm, '$1• ');
